import type { CancelTokenSource } from 'axios';
import type { Editor } from '@tiptap/react';

import API from 'utils/api';
import { EntityModules } from 'core/constants';

const MAX_HEADER_FOOTER_IMAGE_HEIGHT = 96;

// Explicit width/height let Pages measure the band at the right height before the image loads.
const getInsertDimensions = async (file: File): Promise<{ width: number; height: number } | null> => {
  try {
    const bitmap = await createImageBitmap(file);
    let { width, height } = bitmap;
    bitmap.close();
    if (!width || !height) return null;
    if (height > MAX_HEADER_FOOTER_IMAGE_HEIGHT) {
      width = Math.round((width / height) * MAX_HEADER_FOOTER_IMAGE_HEIGHT);
      height = MAX_HEADER_FOOTER_IMAGE_HEIGHT;
    }
    return { width, height };
  } catch {
    return null;
  }
};

/**
 * Uploads through the document's file endpoint, so the header or footer stores a non-expiring
 * `files/{uuid}/download/` link.
 */
export const uploadHeaderFooterImage = async (
  editor: Editor,
  entityId: string,
  file: File,
  cancelTokenSource: CancelTokenSource,
  isStillOpen: () => boolean = () => true
): Promise<void> => {
  const dimensions = await getInsertDimensions(file);
  const { data } = await API.entities.uploadEntityFile(
    EntityModules.Documents,
    entityId,
    file,
    () => {},
    cancelTokenSource
  )();
  if (!data.file || editor.isDestroyed || !isStillOpen()) return;
  editor
    .chain()
    .focus()
    .insertContent({ type: 'image', attrs: { src: data.file, ...dimensions } })
    .run();
};
